/**
 * Role-management screen (Owner-only) — Requirement 23.
 *
 * Lets an Owner view the tenant's users and each user's current role, pick a
 * new role, confirm the change, and see the result. It renders one of two ways
 * depending on the signed-in user's role:
 *
 *   - Owner (Req 23.1): the user list + a per-user role selector + an assign
 *     control, with a confirmation step identifying the target user and the
 *     selected role (Req 23.3). On a Platform-confirmed change it shows a
 *     success User_Message and the updated role in the list (Req 23.4); on a
 *     Platform denial it shows an unchanged-role User_Message mapped from the
 *     backend error envelope via {@link toUserMessage} (Req 23.5).
 *   - Non-Owner (Req 23.2): NO assignment controls — only a restricted
 *     User_Message. Screen visibility is NOT the access control: the Platform
 *     enforces authorization server-side (design.md §10 / Req 12.4-12.6). The
 *     injected {@link RoleManagementScreenProps.onAssignRole} represents that
 *     server call, which itself re-checks Owner-only, so even if a non-Owner
 *     reached the controls the mutation would be denied.
 *
 * Boundary note: `web` may import shared TYPES only, never backend runtime. The
 * four RBAC roles are mirrored here as {@link TenantRole} (matching the backend
 * `Owner|Manager|Staff|Accountant`), and the real `assignRole` service call is
 * INJECTED as {@link RoleManagementScreenProps.onAssignRole} rather than
 * imported from core-api. The result is a shared `ErrorEnvelope` on denial.
 *
 * All user-facing text resolves through {@link useT} (Req 22.11) — no hardcoded
 * literals — so it is translatable without changing structure/layout.
 */
import { useState, type ReactElement } from "react";
// Type-only import from the shared foundation contract (web imports types only).
import type { ErrorEnvelope } from "@avos/foundation";
import { useT } from "../i18n/index.js";
import {
  UserMessage,
  toUserMessage,
  type UserMessageModel,
} from "../components/UserMessage.js";

/**
 * The four tenant RBAC roles (Req 12.1 / 23.3), mirrored from the backend
 * `Owner|Manager|Staff|Accountant`. Declared locally because `web` must not
 * import core-api runtime; kept in one place so the selector and display map
 * cannot drift.
 */
export const TENANT_ROLES = ["Owner", "Manager", "Staff", "Accountant"] as const;

/** A tenant RBAC role. */
export type TenantRole = (typeof TENANT_ROLES)[number];

/** A tenant user as presented in the role list (Req 23.1). */
export interface RoleManagedUser {
  /** Stable user id, passed to {@link RoleManagementScreenProps.onAssignRole}. */
  readonly id: string;
  /** A human-readable display name / email for the user. */
  readonly displayName: string;
  /** The user's current role. */
  readonly role: TenantRole;
}

/**
 * The outcome of the injected role-assignment call.
 *
 * On success it carries the affected user's confirmed new role (mirrors the
 * backend `AssignRoleResult` without importing it). On failure it carries a
 * shared {@link ErrorEnvelope} (e.g. `AUTHZ_DENIED`) that the screen maps to a
 * User_Message via {@link toUserMessage} (Req 23.5).
 */
export type AssignRoleOutcome =
  | { readonly ok: true; readonly affectedUserId: string; readonly newRole: TenantRole }
  | { readonly ok: false; readonly error: ErrorEnvelope };

/**
 * The injected server call representing role assignment (design: the actual
 * mutation goes through the Owner-only, server-enforced `assignRole`). The
 * screen never enforces authorization itself — it just surfaces the outcome.
 */
export type OnAssignRole = (
  userId: string,
  newRole: TenantRole,
) => Promise<AssignRoleOutcome>;

/** Props for {@link RoleManagementScreen}. */
export interface RoleManagementScreenProps {
  /**
   * The signed-in user's role. Decides the restricted view (Req 23.2): only
   * `"Owner"` sees the controls. This is a presentation gate ONLY — the
   * Platform is the authoritative access control.
   *
   * Optional and fail-closed: when the host has not injected a role (e.g. the
   * bare screen-registry render used by the SSR shell), it defaults to a
   * NON-Owner view so no controls leak. The host (and tests, task 16.4) inject
   * the real role.
   */
  readonly currentUserRole?: TenantRole;
  /** The tenant's users to list, injected by the host (Req 23.1). */
  readonly users?: readonly RoleManagedUser[];
  /**
   * The injected role-assignment call. Optional so the screen can render its
   * list/controls in SSR/isolation without a wired backend; the assign action
   * is only enabled once a handler is present.
   */
  readonly onAssignRole?: OnAssignRole;
}

/** A per-user transient banner: a success status or a denial error. */
interface UserBanner {
  readonly severity: "status" | "error";
  readonly message: UserMessageModel;
}

/**
 * Resolve a role's translatable display label (Req 22.11 / 23.3). Falls back to
 * the raw role via the catalog's own missing-id fallback.
 */
function roleLabel(t: ReturnType<typeof useT>, role: TenantRole): string {
  return t(`admin.roles.role.${role}`);
}

export default function RoleManagementScreen({
  currentUserRole = "Staff",
  users = [],
  onAssignRole,
}: RoleManagementScreenProps = {}): ReactElement {
  const t = useT();

  // Per-user selected role (defaults to the user's current role) and per-user
  // pending confirmation + result banner. Keyed by user id.
  const [selectedRoles, setSelectedRoles] = useState<Record<string, TenantRole>>(
    {},
  );
  const [confirmingUserId, setConfirmingUserId] = useState<string | null>(null);
  const [banners, setBanners] = useState<Record<string, UserBanner>>({});
  // Local view of each user's role so a confirmed change updates the list
  // (Req 23.4) without needing the host to re-inject `users`.
  const [displayedRoles, setDisplayedRoles] = useState<Record<string, TenantRole>>(
    {},
  );

  // Non-Owner: no controls, only the restricted User_Message (Req 23.2). The
  // Platform — not this hidden screen — is the real access control.
  if (currentUserRole !== "Owner") {
    return (
      <section aria-labelledby="screen-heading" data-screen="roleManagement">
        <h1 id="screen-heading">{t("admin.roles.title")}</h1>
        <UserMessage
          severity="error"
          message={{
            what: t("admin.roles.restricted.what"),
            why: t("admin.roles.restricted.why"),
            next: t("admin.roles.restricted.next"),
          }}
        />
      </section>
    );
  }

  const roleFor = (user: RoleManagedUser): TenantRole =>
    displayedRoles[user.id] ?? user.role;

  const selectionFor = (user: RoleManagedUser): TenantRole =>
    selectedRoles[user.id] ?? roleFor(user);

  const beginConfirm = (userId: string): void => {
    setConfirmingUserId(userId);
  };

  const cancelConfirm = (): void => {
    setConfirmingUserId(null);
  };

  const confirmAssign = async (user: RoleManagedUser): Promise<void> => {
    setConfirmingUserId(null);
    if (onAssignRole === undefined) {
      return;
    }
    const newRole = selectionFor(user);
    const outcome = await onAssignRole(user.id, newRole);
    if (outcome.ok) {
      // Platform confirmed the change (Req 23.4): update the displayed role and
      // show a success User_Message.
      setDisplayedRoles((prev) => ({ ...prev, [user.id]: outcome.newRole }));
      setBanners((prev) => ({
        ...prev,
        [user.id]: {
          severity: "status",
          message: {
            what: t("admin.roles.assignSuccess.what"),
            why: t("admin.roles.assignSuccess.why", {
              user: user.displayName,
              role: roleLabel(t, outcome.newRole),
            }),
            next: t("admin.roles.assignSuccess.next"),
          },
        },
      }));
    } else {
      // Platform denied the change (Req 23.5): show the mapped User_Message and
      // leave the displayed role UNCHANGED from before the attempt.
      setBanners((prev) => ({
        ...prev,
        [user.id]: {
          severity: "error",
          message: toUserMessage(outcome.error),
        },
      }));
    }
  };

  return (
    <section aria-labelledby="screen-heading" data-screen="roleManagement">
      <h1 id="screen-heading">{t("admin.roles.title")}</h1>
      <p>{t("admin.roles.prompt")}</p>

      <table data-testid="role-management-table">
        <thead>
          <tr>
            <th scope="col">{t("admin.roles.userHeader")}</th>
            <th scope="col">{t("admin.roles.currentRoleHeader")}</th>
            <th scope="col">{t("admin.roles.newRoleHeader")}</th>
            <th scope="col">{t("admin.roles.actionHeader")}</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => {
            const currentRole = roleFor(user);
            const selected = selectionFor(user);
            const banner = banners[user.id];
            const isConfirming = confirmingUserId === user.id;
            const selectId = `role-select-${user.id}`;
            return (
              <tr key={user.id} data-testid={`role-row-${user.id}`}>
                <td data-testid="role-user-name">{user.displayName}</td>
                <td data-testid="role-current" data-role={currentRole}>
                  {roleLabel(t, currentRole)}
                </td>
                <td>
                  <label htmlFor={selectId} className="visually-hidden">
                    {t("admin.roles.roleSelectLabel", { user: user.displayName })}
                  </label>
                  <select
                    id={selectId}
                    data-testid={`role-select-${user.id}`}
                    value={selected}
                    onChange={(event) =>
                      setSelectedRoles((prev) => ({
                        ...prev,
                        [user.id]: event.target.value as TenantRole,
                      }))
                    }
                  >
                    {TENANT_ROLES.map((role) => (
                      <option key={role} value={role}>
                        {roleLabel(t, role)}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  {isConfirming ? (
                    // Confirmation control identifying target user + selected
                    // role before submitting (Req 23.3).
                    <div
                      role="group"
                      aria-label={t("admin.roles.confirm")}
                      data-testid={`role-confirm-${user.id}`}
                    >
                      <p data-testid="role-confirm-prompt">
                        {t("admin.roles.confirmPrompt", {
                          user: user.displayName,
                          role: roleLabel(t, selected),
                        })}
                      </p>
                      <button
                        type="button"
                        data-testid={`role-confirm-submit-${user.id}`}
                        onClick={() => {
                          void confirmAssign(user);
                        }}
                      >
                        {t("admin.roles.confirm")}
                      </button>
                      <button
                        type="button"
                        data-testid={`role-cancel-${user.id}`}
                        onClick={cancelConfirm}
                      >
                        {t("admin.roles.cancel")}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      data-testid={`role-assign-${user.id}`}
                      onClick={() => beginConfirm(user.id)}
                    >
                      {t("admin.roles.assign")}
                    </button>
                  )}
                </td>
                {banner !== undefined ? (
                  <td>
                    <UserMessage severity={banner.severity} message={banner.message} />
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
